import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useStepRegister } from 'context/login/StepRegister';
import { useUser } from 'context/user/User';
import { StyledForm } from 'styled/UI/StyledForm';
import { useForm } from 'react-hook-form';
import fieldValidations from 'helpers/fieldValidations';
import buttonText from 'texts/buttons/buttonText';
import step from 'helpers/system/step';
import userApi from 'api/requests/user';
import Loader from 'components/_UI/Loader';
import fieldMessages from 'texts/messages/field';
import { User, Mail, Lock, ShieldCheck, Eye, EyeOff, ArrowRight } from 'lucide-react';
import {
    Title, TitleAccent, Subtitle, FieldGroup, FieldLabel, InputShell, IconBubble,
    TogglePassword, FieldError, SubmitButton, LoaderWrapper
} from '../common/AuthForm';

const errorMessages = {
    unique: 'E-mail já cadastrado',
    fail: 'Ocorreu um erro, tente novamente.'
}

export default function FastOnboarding() {
    const { stepRegister, setStepRegister } = useStepRegister();
    const { user, setUser } = useUser();

    const [validateEmailError, setValidateEmailError] = useState(false);
    const [emailError, setEmailError] = useState(false);
    const [loading, setLoading] = useState(false)
    const [equalPasswordError, setEqualPasswordError] = useState(false)
    const [back,setBack] = useState(false)
    const [visiblePasswords, setVisiblePasswords] = useState({ password: false, confirmPassword: false })

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors }
    } = useForm({
        defaultValues: {
            firstName: user?.first_name || '',
            lastName: user?.last_name || '',
            email: user?.email || '',
        }
    });

    useEffect(() => {
     if(!back) {
        reset({
            firstName: '',
            lastName: '',
            email: ''
        })
     }
    }, [back])
    

    async function validateData({ firstName, lastName, password, confirmPassword, email }) {
        let hasError = false

        setLoading(true)

        try {
            const { data } = await userApi.dataEnable(
                {
                    email,
                });
            hasError = !data.email
        } catch (error) {
            hasError = 'fail'
        } finally {
            setLoading(false)
        }

        if (hasError === 'fail') {
            setValidateEmailError(true)
            return;
        }

        if (hasError) {
            setEmailError(true)
            return
        }

        nextStep(firstName, lastName, password, confirmPassword, email)
    }

    const nextStep = (firstName, lastName, password, confirmPassword, email) => {
        setValidateEmailError(false)
        setEmailError(false)
        setEqualPasswordError(false)
        
        if (password !== confirmPassword) {
            setEqualPasswordError(true)   
            return
        }
        
        setUser({
            ...user,
            first_name: firstName,
            last_name: lastName,
            password,
            email
        });
        setBack(true)
        setStepRegister(step.next(stepRegister))
    };

    const passwordToggle = (field) => (
        <TogglePassword
            type='button'
            onClick={() => setVisiblePasswords((prev) => ({ ...prev, [field]: !prev[field] }))}
            aria-label={visiblePasswords[field] ? 'Ocultar senha' : 'Mostrar senha'}
        >
            {visiblePasswords[field] ? <EyeOff size={18} strokeWidth={1.8} /> : <Eye size={18} strokeWidth={1.8} />}
        </TogglePassword>
    );

    return (
        <>
            <Title>Crie sua <TitleAccent>conta</TitleAccent></Title>
            <Subtitle>Preencha suas informações básicas<br />para começar a explorar.</Subtitle>

            <StyledForm onSubmit={handleSubmit(validateData)} noValidate>
                <NameRow>
                    <FieldGroup $first>
                        <FieldLabel htmlFor='signup-first-name'>Nome</FieldLabel>
                        <InputShell $invalid={!!errors.firstName}>
                            <IconBubble aria-hidden='true'><User size={16} strokeWidth={1.8} /></IconBubble>
                            <input
                                id='signup-first-name'
                                autoComplete='given-name'
                                placeholder='Seu nome'
                                aria-invalid={errors.firstName ? 'true' : undefined}
                                {...register('firstName', fieldValidations.name())}
                            />
                        </InputShell>
                        {errors.firstName?.message && (
                            <FieldError role='alert'>{errors.firstName?.message}</FieldError>
                        )}
                    </FieldGroup>

                    <FieldGroup $first>
                        <FieldLabel htmlFor='signup-last-name'>Sobrenome</FieldLabel>
                        <InputShell $invalid={!!errors.lastName}>
                            <input
                                id='signup-last-name'
                                autoComplete='family-name'
                                placeholder='Seu sobrenome'
                                aria-invalid={errors.lastName ? 'true' : undefined}
                                {...register('lastName', fieldValidations.name('sobrenome'))}
                            />
                        </InputShell>
                        {errors.lastName?.message && (
                            <FieldError role='alert'>{errors.lastName?.message}</FieldError>
                        )}
                    </FieldGroup>
                </NameRow>

                <FieldGroup>
                    <FieldLabel htmlFor='signup-email'>E-mail</FieldLabel>
                    <InputShell $invalid={!!errors.email || emailError}>
                        <IconBubble aria-hidden='true'><Mail size={16} strokeWidth={1.8} /></IconBubble>
                        <input
                            id='signup-email'
                            type='email'
                            autoComplete='email'
                            placeholder='seu@email.com'
                            aria-invalid={errors.email || emailError ? 'true' : undefined}
                            {...register('email', fieldValidations.email())}
                        />
                    </InputShell>
                    {(errors.email?.message || emailError) && (
                        <FieldError role='alert'>{errors.email?.message || errorMessages.unique}</FieldError>
                    )}
                </FieldGroup>

                <FieldGroup>
                    <FieldLabel htmlFor='signup-password'>Senha</FieldLabel>
                    <InputShell $invalid={!!errors.password}>
                        <IconBubble aria-hidden='true'><Lock size={17} strokeWidth={1.8} /></IconBubble>
                        <input
                            id='signup-password'
                            type={visiblePasswords.password ? 'text' : 'password'}
                            autoComplete='new-password'
                            placeholder='Crie uma senha'
                            aria-invalid={errors.password ? 'true' : undefined}
                            {...register('password', fieldValidations.password('senha'))}
                        />
                        {passwordToggle('password')}
                    </InputShell>
                    {errors.password?.message && (
                        <FieldError role='alert'>{errors.password?.message}</FieldError>
                    )}
                </FieldGroup>

                <FieldGroup>
                    <FieldLabel htmlFor='signup-confirm-password'>Confirme sua senha</FieldLabel>
                    <InputShell $invalid={!!errors.confirmPassword || equalPasswordError}>
                        <IconBubble aria-hidden='true'><ShieldCheck size={17} strokeWidth={1.8} /></IconBubble>
                        <input
                            id='signup-confirm-password'
                            type={visiblePasswords.confirmPassword ? 'text' : 'password'}
                            autoComplete='new-password'
                            placeholder='Repita a senha'
                            aria-invalid={errors.confirmPassword || equalPasswordError ? 'true' : undefined}
                            {...register('confirmPassword', fieldValidations.password('confirmação de senha'))}
                        />
                        {passwordToggle('confirmPassword')}
                    </InputShell>
                    {(errors.confirmPassword?.message || equalPasswordError) && (
                        <FieldError role='alert'>
                            {errors.confirmPassword?.message || fieldMessages.passwordMustBeEqual}
                        </FieldError>
                    )}
                </FieldGroup>

                {loading ? (
                    <LoaderWrapper>
                        <Loader />
                    </LoaderWrapper>
                ) : (
                    <NextButton type='submit'>
                        <span>{buttonText.next}</span>
                        <ArrowRight size={18} strokeWidth={2} aria-hidden='true' />
                    </NextButton>
                )}

                {validateEmailError && (
                    <FormError role='alert'>{errorMessages.fail}</FormError>
                )}
            </StyledForm>
        </>
    )
};

const NameRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
`;

const NextButton = styled(SubmitButton)`
    margin-top: 26px;
`;

const FormError = styled(FieldError)`
    margin-top: 14px;
    text-align: center;
`;
